import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fdt } from "../api";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import { StatusPill, SERVICE_LABELS } from "../components/bits";
import { Search } from "lucide-react";

export default function TrackPage() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (r) => {
    const value = (r ?? ref).trim();
    if (!value) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(value)}`);
      setResult(data);
    } catch { setError("No shipment found with that reference."); }
    setLoading(false);
  };

  React.useEffect(() => { if (params.get("ref")) track(params.get("ref")); /* eslint-disable-next-line */ }, []);

  const jobs = [];
  if (result?.pickup?.lat) jobs.push({ id: "p", status: result.status, pickup: { lat: result.pickup.lat, lng: result.pickup.lng } });
  const drivers = result?.courier_location ? [{ driver_id: "c", lat: result.courier_location.lat, lng: result.courier_location.lng, name: result.courier_name }] : [];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="track-page">
      <Seo title="Track your delivery | CourierRadar" description="Live GPS tracking for CourierRadar deliveries across Europe." />
      <h1 className="text-3xl font-extrabold tracking-tight text-black">Track a delivery</h1>
      <p className="text-sm text-zinc-500 mt-2 mb-6">Enter your tracking reference (e.g. CR-1A2B3C4D).</p>
      <div className="flex gap-2">
        <input data-testid="track-input" value={ref} onChange={(e) => setRef(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && track()} placeholder="CR-…"
          className="flex-1 border border-zinc-300 focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/20 rounded-lg text-sm bg-white px-4 py-3 outline-none font-mono" />
        <button data-testid="track-submit-btn" onClick={() => track()} disabled={loading}
          className="bg-black text-white hover:bg-zinc-800 font-medium transition-all px-5 py-3 rounded-lg text-sm flex items-center gap-2">
          <Search className="w-4 h-4" /> Track
        </button>
      </div>
      {error && <p data-testid="track-error" className="text-sm text-red-600 mt-4">{error}</p>}
      {result && (
        <div data-testid="track-result" className="mt-8 space-y-5">
          <div className="cr-card !p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono font-bold">{result.ref}</span>
              <StatusPill status={result.status} />
            </div>
            <p className="text-xs text-zinc-500">{SERVICE_LABELS[result.service_level]} · {result.pickup?.city || "—"} → {result.delivery?.city || "—"}</p>
          </div>
          <div className="rounded-xl overflow-hidden border border-black/5">
            <MapView height="300px" jobs={jobs} drivers={drivers} />
          </div>
          <div className="cr-card !p-5">
            <h2 className="text-sm font-bold mb-3">Updates</h2>
            {[...(result.timeline || [])].reverse().map((t, i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-zinc-50 last:border-0">
                <span className="text-sm capitalize font-medium">{t.status.replace(/_/g, " ")}</span>
                <span className="text-xs text-zinc-400">{fdt(t.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
